import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];
const HERO_IMG = "https://images.pexels.com/photos/3762765/pexels-photo-3762765.jpeg?auto=compress&cs=tinysrgb&w=1600";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease, delay: 0.4 + i * 0.14 },
  }),
};

function Line({ children, i }) {
  return (
    <span className="line-mask">
      <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="block">
        {children}
      </motion.span>
    </span>
  );
}

export function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-32 pb-14 md:pb-20">
      <div className="absolute inset-0 -z-10 grid grid-cols-12">
        {Array.from({ length: 13 }).map((_, i) => (
          <div key={i} className="border-l border-ink/[0.04] h-full" />
        ))}
      </div>

      <div className="max-w-7xl mx-auto w-full px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <motion.div style={{ y: textY }} className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="mb-8"
          >
            <span className="inline-flex items-center gap-3 text-[11px] md:text-xs tracking-[0.25em] uppercase font-semibold text-muted">
              <span className="h-px w-10 bg-sage" />
              Studio medico · Genova
            </span>
          </motion.div>

          <h1 data-testid="hero-title" className="font-serif font-light leading-[0.92] tracking-tight text-[13vw] sm:text-[11vw] lg:text-[6.4vw]">
            <Line i={0}>Laser, Skin Quality</Line>
            <Line i={1}>e <span className="italic text-sagedark">Tricologia</span></Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease }}
            className="mt-9 max-w-xl text-lg md:text-xl text-muted font-light leading-relaxed"
          >
            Tecnologie avanzate e trattamenti rigenerativi per migliorare la qualità
            della pelle, trattare cicatrici, discromie e lesioni vascolari e costruire
            percorsi personalizzati per la salute dei capelli.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.9, ease }}
            className="mt-5 text-sm md:text-base text-graphite/70 italic font-serif"
          >
            Prima comprendere il problema. Poi scegliere la tecnologia.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <button
              data-testid="hero-book-button"
              onClick={onBook}
              className="group relative inline-flex items-center overflow-hidden border border-ink px-9 py-4 text-xs uppercase tracking-[0.2em]"
            >
              <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="relative group-hover:text-canvas transition-colors duration-500">Prenota una valutazione</span>
            </button>
            <button
              onClick={() => document.getElementById("aree")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-ink link-underline"
            >
              <ArrowDown className="w-4 h-4" /> Scopri i trattamenti
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1.2, ease }}
          className="lg:col-span-4 relative"
        >
          <div className="relative h-[42vh] lg:h-[60vh] w-full overflow-hidden">
            <motion.img
              src={HERO_IMG}
              alt="Studio Valle · interno clinica"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-ink/10" />
          </div>
          <div className="mt-4 flex justify-between text-[11px] uppercase tracking-[0.2em] text-muted">
            <span>Studio · Genova</span>
            <span>Est. medico</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
